import React, { useEffect, useMemo, useState } from 'react';
import { Paper, Snackbar, Tooltip } from '@mui/material';
import { DataGrid, GridActionsColDef, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';
import PlannenActionMenu from './PlannenActionMenu';
import { InfraPlan } from '../../../models/plan/plan';

import { helperService } from '../../../services/helper.service';
import { contentService } from '../../../services/content.service';
import { mapService } from '../../../services/map.service';
import { eventBus } from '../../../services/event-emitter.service';


interface OverzichtPlannenProps {
  plannen?: InfraPlan[];
  activeParticipant?: boolean;
  mijnOrganisatieId?: string;
  refreshKey?: number;
}

const OverzichtPlannen: React.FC<OverzichtPlannenProps> = ({ 
  plannen,
  activeParticipant,
  mijnOrganisatieId,
  refreshKey
}) => {
  const [statusOrderMap, setStatusOrderMap] = useState<Record<string, number>>({});

  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [openSnackbar, setOpenSnackbar] = useState<boolean>(false);
  const [hasFetchedData, setHasFetchedData] = useState(false);

  const gridColumns = useMemo<(GridColDef<any> | GridActionsColDef<InfraPlan>)[]>(() => {
    const baseColumns: (GridColDef<any> | GridActionsColDef<InfraPlan>)[] = [
      // {
      //   field: 'planId',
      //   headerName: 'Nummer',
      //   flex: 1,
      //   renderCell: (params: any) => <>{params.row.planId}</>,
      // },
      {
        field: 'planNaam',
        headerName: 'Naam',
        flex: 3,
        renderCell: (params: any) => (
          <>
            {params.row.planId ? (
              <button
                type="button"
                className="table-link-button"
                onClick={(event) => {
                  onClickPlanLink(params.row);
                }}
              >
                {params.row.planNaam}
              </button>
            ) : (
              <Tooltip title="Dit plan wordt niet met jouw organisatie gedeeld" placement="right" arrow>
                <span>Dit plan wordt niet met jouw organisatie gedeeld</span>
              </Tooltip>
            )}
          </>
        ),
      },
      {
        field: 'discipline',
        headerName: 'Discipline',
        flex: 0.5,
        renderCell: (params: any) => {
          const camelDiscipline = helperService.toCamelCase(params.row.discipline);
          return (
            <Tooltip title={params.row.discipline} placement="right" arrow>
              <span className={`category-icon category-icon-small ${camelDiscipline}-icon-white`}>
                {/* <span>{camelDiscipline}</span> */}
              </span>
            </Tooltip>
          );
        },
      },
      {
        field: 'organizationName',
        headerName: 'Organisatie',
        flex: 1,
        renderCell: (params: any) => <>{params.row.organizationName}</>,
      },
      {
        field: 'planType',
        headerName: 'Type',
        flex: 1,
        renderCell: (params: any) => <>{params.row.planType}</>,
      },
      {
        field: 'planStatus',
        headerName: 'Status',
        flex: 1,
        renderCell: (params: any) => <>{params.row.planStatus}</>,
      },
      // {
      //   field: 'planStartDatum',
      //   headerName: 'Periode',
      //   flex: 2,
      //   renderCell: (params: any) => {
      //     const { planStartDatum, planEindDatum } = params.row;
      
      //     const formatDate = (dateString: string) => {
      //       const date = new Date(dateString);
      //       return isNaN(date.getTime())
      //         ? ''
      //         : date.toLocaleDateString('nl-NL', {
      //             day: '2-digit',
      //             month: '2-digit', // short
      //             year: 'numeric',
      //           });
      //     };
      
      //     const start = formatDate(planStartDatum);
      //     const end = formatDate(planEindDatum);
      
      //     if (!start) return ''; // geen startdatum = niks tonen
      
      //     return end ? `${start} – ${end}` : start;
      //   }
      // },
      {
        field: 'planStartDatum',
        headerName: 'Start',
        flex: 1,
        renderCell: (params: any) => {
          const value = params.row.planStartDatum;
          if (!value) return '';
          const date = new Date(value);
          return isNaN(date.getTime())
            ? ''
            : date.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
        }
      },
      {
        field: 'planEindDatum',
        headerName: 'Eind',
        flex: 1,
        renderCell: (params: any) => {
          const value = params.row.planEindDatum;
          if (!value) return '';
          const date = new Date(value);
          return isNaN(date.getTime())
            ? ''
            : date.toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' });
        }
      },
      {
        field: 'inclusionRequestStatus',
        headerName: 'Deelname',
        flex: 1,
        renderCell: (params: any) => (
          <>
            {getInclusionRequestStatusLabel(params.row.inclusionRequestStatus)}
          </>
        ),
        sortComparator: (a: string, b: string) => {
          const order1 = statusOrderMap[a] ?? Number.MAX_SAFE_INTEGER;
          const order2 = statusOrderMap[b] ?? Number.MAX_SAFE_INTEGER;
          return order1 - order2;
        }
      },
      {
        field: 'actions',
        headerName: '',
        width: 70,
        sortable: false,
        filterable: false,
        renderCell: (params: GridRenderCellParams<InfraPlan>) => (
          <>
            { params.row.planId && (
              <PlannenActionMenu 
                params={params} 
                activeParticipant={activeParticipant} 
                mijnOrganisatieId={mijnOrganisatieId} />
            )}
          </>
        )
      }
    ];
    
    return baseColumns;
  }, [hasFetchedData, plannen]);

  useEffect(() => {
    const statusses = contentService.getInclusionRequestStatusses();
    const orderMap = statusses.reduce((acc: Record<string, number>, status: any) => {
      acc[status.value] = status.order;
      return acc;
    }, {});
    setStatusOrderMap(orderMap);
  }, []);

  useEffect(() => {
    if (plannen) {
      setTotalCount(plannen.length);
      setLoading(false);
    }

    // Ensure the fetch happens only once
    if (!hasFetchedData) {
      setHasFetchedData(true);
    }
  }, [hasFetchedData, refreshKey]); // Empty dependency array ensures this runs once when the component mounts

  const onClickPlanLink = (plan: InfraPlan) => {
    if (!mapService.isValidBbox(plan?.boundingBox)) {
      setSnackbarMessage('De begrenzing van dit plan is ongeldig of onvolledig');
      setOpenSnackbar(true);
      return;
    }

    eventBus.emit('zoomInOnPlanBbox', plan.boundingBox);
  };

  const getInclusionRequestStatusLabel = (id: string) => {
    const statusLabel = contentService.getInclusionRequestStatusLabel(id);
    return statusLabel ?? id; // fallback to id if label not found
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openSnackbar}
        autoHideDuration={2000}
        onClose={() => setOpenSnackbar(false)}
        message={snackbarMessage ? snackbarMessage : 'Er is een fout opgetreden'}
        key="topcenter"
      />

      <div className="grid-responsive">
        <Paper sx={{ width: '100%' }}>
          <DataGrid
            rows={plannen}
            columns={gridColumns}
            getRowId={(row) => row.infplId || ''}
            getRowClassName={(params) => `discipline-row ${helperService.toCamelCase(params.row.discipline)}-border`}
            disableColumnFilter
            disableRowSelectionOnClick
            sx={{ border: 0 }}
            pagination
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 10, page: 0 },
              },
              sorting: {
                sortModel: [{ field: 'inclusionRequestStatus', sort: 'asc' }],
              },
            }}
          />
        </Paper>
      </div>

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}
    </>
  );
};

export default OverzichtPlannen;
